/**
 * THE PER-LINE REMARK FIELD, reachable at BOTH decision states (D14).
 * NOT WIRED, NOT PRODUCTION.
 *
 * WHAT IT IS, AND WHAT IT IS NOT. It is the SHIPPED remark editor, lifted --
 * the same `clinic-workbench__input cockpit-plan__note` textarea, the same
 * label from the label dictionary, the same placeholder, the same separate
 * Save act, folded behind the same `cockpit-plan__change` disclosure the
 * consult row already uses (`ConsultWorkspacePage.tsx:2565-2637`). No new
 * primitive, no new vocabulary, no new endpoint.
 *
 * WHY IT IS HANDED OVER AS A COMPONENT AT ALL. In the shipped page that editor
 * lives ONLY inside a DECIDED line's "Change" disclosure. An UNDECIDED
 * treatment therefore has nowhere to record a remark today -- which is half of
 * what D14 asks for, and is not reachable on the surface as it stands. This
 * component is the same field with that gate removed, so Lane 1 can offer it at
 * both states with one mount rather than restructuring the row's branches.
 *
 * A DELIBERATE SAVE, FOR THE SAME REASON THE DECISION HAS ONE. Typing stages;
 * a Save appears only when there is something uncommitted and commits it. The
 * caller's existing audited write is the only writer -- this component fires no
 * fetch -- and the patch it asks for names ONLY the remark, so the endpoint's
 * absent-means-untouched contract leaves the decision and the assignment alone.
 *
 * SERVER-CONFIRMED MEANS SERVER-CONFIRMED. `itemNote` is the LAST CONFIRMED
 * remark. On a failure the staged text is kept in the box (losing a doctor's
 * typing is its own defect) while the OUTCOME LINE names the stored value, so
 * what is recorded and what is merely typed are never confused.
 *
 * THE OUTCOME LINE SITS OUTSIDE THE DISCLOSURE. A failure rendered inside a
 * door the doctor has since closed is a failure nobody is told about, which is
 * the same defect as a silent save. The caller places `<PlanRemarkOutcome>`
 * beside the field, not within it.
 */
import { useState } from 'react';
import { usePlanControlDraft, type PlanDraftOwner } from './consult-autosave';
import type { JSX } from 'react';

import AcButton from '@/dashboard/ui/AcButton';
import { CASE_SHEET_LABELS } from '@/dashboard/clinical-workbench/case-sheet-labels';
import {
  PLAN_DECISION_FAILURE_COPY,
  PLAN_DECISION_LAST_CONFIRMED_PREFIX,
  PLAN_DECISION_UNCONFIRMED_PREFIX,
  type PlanDecisionOutcome,
} from '@/dashboard/doctor/PlanDecisionPicklist';

export const PLAN_REMARK_SAVE_LABEL = 'Save remark';
/** What the outcome line calls a line with no stored remark. */
export const PLAN_REMARK_NONE_COPY = 'none';

export interface PlanRemarkFieldProps {
  readonly itemId: number;
  readonly procedureName: string;
  /** The LAST CONFIRMED remark. '' = none recorded. */
  readonly itemNote: string;
  /** Commit the staged remark. The caller owns the write. */
  readonly onCommit: (itemNote: string) => void | Promise<boolean>;
  readonly draftOwner?: PlanDraftOwner;
  readonly outcome?: PlanDecisionOutcome;
  /** True while ANY write on this line is unanswered -- not just this one. */
  readonly busy?: boolean;
  /** Start the disclosure open (a review driver, or a row being corrected). */
  readonly defaultOpen?: boolean;
  /** MOUNT-TIME IDENTITY, added by #780's integration -- see PlanDoctorPicker
   *  for the same note. This field REPLACES the shipped Change disclosure's
   *  textarea and Save, and it brings its own door; putting it inside the old
   *  door would be a door inside a door. So the mount hands it the shipped
   *  door's testid instead, and the surface keeps ONE door per fact. */
  readonly doorTestId?: string;
}

export default function PlanRemarkField({
  itemId,
  procedureName,
  itemNote,
  onCommit,
  outcome = { kind: 'idle' },
  busy = false,
  defaultOpen = false,
  doorTestId, draftOwner,
}: PlanRemarkFieldProps): JSX.Element {
  /** The STAGED text. null = nothing typed, so the box shows what is stored. */
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? itemNote;
  const dirty = draft !== null && draft !== itemNote;
  const saving = outcome.kind === 'saving';
  usePlanControlDraft(draftOwner, `plan-remark-${itemId}`, { dirty, signature: JSON.stringify(draft),
    pending: saving, commit: () => draft !== null ? onCommit(draft) : true, reset: () => setDraft(null),
  });

  return (
    <details className="cockpit-plan__change cockpit-plan__change--note" open={defaultOpen}>
      <summary
        className="cockpit-plan__change-summary"
        data-testid={doorTestId ?? `plan-remark-${itemId}-door`}
      >
        {CASE_SHEET_LABELS.itemNote}
      </summary>
      <div className="cockpit-plan__change-body cockpit-plan__change-body--note">
        <textarea
          id={`plan-remark-${itemId}`}
          className="clinic-workbench__input cockpit-plan__note"
          aria-label={`${CASE_SHEET_LABELS.itemNote} for ${procedureName}`}
          placeholder={CASE_SHEET_LABELS.itemNote}
          rows={2}
          value={shown}
          disabled={busy}
          data-testid={`plan-remark-${itemId}`}
          onChange={(e) => setDraft(e.target.value)}
        />
        {dirty ? (
          <AcButton
            size="sm"
            variant="secondary"
            disabled={busy}
            data-testid={`plan-remark-${itemId}-save`}
            onClick={() => {
              if (draft === null) return;
              onCommit(draft);
            }}
          >
            {saving ? 'Saving...' : PLAN_REMARK_SAVE_LABEL}
          </AcButton>
        ) : null}
      </div>
    </details>
  );
}

/**
 * The remark's outcome line. SEPARATE from the field on purpose -- see the
 * header: it must render outside the disclosure, where a doctor sees it whether
 * or not the door is open.
 */
export function PlanRemarkOutcome({
  itemId,
  itemNote,
  outcome,
}: {
  readonly itemId: number;
  readonly itemNote: string;
  readonly outcome: PlanDecisionOutcome;
}): JSX.Element | null {
  if (outcome.kind !== 'failed') return null;
  return (
    <p
      role="alert"
      data-testid={`plan-remark-${itemId}-outcome`}
      data-failure={outcome.failure}
    >
      <strong>{PLAN_DECISION_UNCONFIRMED_PREFIX}.</strong>{' '}
      {PLAN_DECISION_FAILURE_COPY[outcome.failure]}{' '}
      {PLAN_DECISION_LAST_CONFIRMED_PREFIX} {CASE_SHEET_LABELS.itemNote.toLowerCase()}:{' '}
      {itemNote === '' ? PLAN_REMARK_NONE_COPY : itemNote}.
    </p>
  );
}
